import { Box, MenuItem, Stack, TextField } from "@mui/material";
import { Button, Typography } from "@platform/design-system";
import {
  makeDraftDollarAmount,
  toDraftDollarAmounts,
  type DraftDollarAmount,
} from "@platform/plan-grid-core";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";

import { BenefitDescriptionValueInputs } from "./BenefitDescriptionValueInputs";
import {
  CustomTextEditorFields,
  CustomTextModeToggle,
} from "./CustomTextEditorFields";
import {
  benefitDescriptionEditorShape,
  benefitDescriptionHasError,
  buildBenefitDescriptionSave,
  readDollarAmounts,
  readSingleAmount,
  type BenefitDescriptionEntryLimits,
} from "./benefitDescriptionValues";
import { writeCollapsedSiblings, writeSibling } from "./siblingWrites";
import { useCustomTextEditor } from "./useCustomTextEditor";
import { usePopupEditorKeyboard } from "./usePopupEditorKeyboard";

import type { ICellEditorParams } from "ag-grid-community";
import type { JsonValue } from "../utils/popupCustomText";

export interface BenefitDescriptionTypeOption {
  value: string;
  label: string;
}

export interface BenefitDescriptionCellEditorParams {
  initialType: string | undefined;
  initialAmount: unknown;
  initialMinimum: number | undefined;
  typeKey: string;
  amountKey: string;
  /** Absent for coverages whose benefit description has no Minimum sibling. */
  minimumKey?: string;
  typeOptions: BenefitDescriptionTypeOption[];
  /** Overrides the selected type's default list bounds. */
  entryLimits?: BenefitDescriptionEntryLimits;
  initialCustomText?: string | null;
  onCustomTextChange?: (
    customText: string | null,
    originalValue?: JsonValue,
  ) => void;
}

type Props = ICellEditorParams & BenefitDescriptionCellEditorParams;

/**
 * AG Grid popup cell editor for the (Employee/Spouse/Child) Benefit Description
 * field, whose value shape depends on the selected type: a single whole-dollar
 * amount, a single percentage, or a list of dollar amounts — plus an optional
 * minimum for the single-value types. On save it writes the type, amount, and
 * minimum back to their collapsed sibling rows.
 */
export const BenefitDescriptionCellEditor = forwardRef<unknown, Props>(
  function BenefitDescriptionCellEditor(props, ref) {
    const {
      initialType,
      initialAmount,
      initialMinimum,
      typeKey,
      amountKey,
      minimumKey,
      typeOptions,
      entryLimits,
      initialCustomText,
      onCustomTextChange,
      api,
      colDef,
      column,
      node,
      stopEditing,
    } = props;

    const [draftType, setDraftType] = useState<string>(initialType ?? "");

    const [draftSingle, setDraftSingle] = useState<string>(
      readSingleAmount(initialAmount),
    );

    const [draftMinimum, setDraftMinimum] = useState<string>(
      initialMinimum == null ? "" : String(initialMinimum),
    );

    const [arrayEntries, setArrayEntries] = useState<DraftDollarAmount[]>(
      toDraftDollarAmounts(readDollarAmounts(initialAmount)),
    );

    const [showErrors, setShowErrors] = useState(false);

    const customText = useCustomTextEditor(
      initialCustomText,
      onCustomTextChange,
      stopEditing,
    );
    const { editingCustomText } = customText;

    const lastValueRef = useRef<unknown>(initialType ?? null);

    useImperativeHandle(ref, () => ({
      getValue: () => lastValueRef.current,
      isPopup: () => true,
      isCancelBeforeStart: () => false,
      isCancelAfterEnd: () => false,
    }));

    const { valueKind, showMinimum, maxEntries, minEntries } =
      benefitDescriptionEditorShape(
        draftType,
        minimumKey !== undefined,
        entryLimits,
      );

    const updateArrayEntry = (index: number, value: string): void => {
      setShowErrors(true);
      setArrayEntries((prev) =>
        prev.map((entry, i) =>
          i === index ? { ...entry, amount: value } : entry,
        ),
      );
    };

    const addArrayEntry = (): void => {
      setArrayEntries((prev) => [...prev, makeDraftDollarAmount()]);
    };

    const removeArrayEntry = (index: number): void => {
      setArrayEntries((prev) => prev.filter((_, i) => i !== index));
    };

    const hasError = benefitDescriptionHasError(
      valueKind,
      draftSingle,
      arrayEntries,
      minEntries,
    );

    const handleSave = (): void => {
      if (editingCustomText) {
        lastValueRef.current = initialType ?? null;
        customText.saveCustomText(initialType);

        return;
      }

      if (hasError) {
        setShowErrors(true);

        return;
      }

      const { amount, minimum } = buildBenefitDescriptionSave(
        valueKind,
        draftSingle,
        showMinimum ? draftMinimum : "",
        arrayEntries,
      );

      lastValueRef.current = draftType || null;

      const columnField = colDef?.field;
      if (initialCustomText) onCustomTextChange?.(null);

      if (columnField && node?.data) {
        writeCollapsedSiblings(
          node as { data: Record<string, unknown> },
          columnField,
          [
            [typeKey, draftType || null],
            [amountKey, amount],
            ...(minimumKey ? [[minimumKey, minimum] as [string, unknown]] : []),
          ],
        );
      }

      stopEditing(true);

      setTimeout(() => {
        writeSibling({ api, column, colDef }, amountKey, amount, {
          silent: true,
        });
        if (minimumKey) {
          writeSibling({ api, column, colDef }, minimumKey, minimum, {
            silent: true,
          });
        }
        writeSibling({ api, column, colDef }, typeKey, draftType || null);
        if (node) api.refreshCells({ rowNodes: [node], force: true });
      }, 0);
    };

    const handleCancel = (): void => {
      lastValueRef.current = initialType ?? null;
      stopEditing(true);
    };

    const containerRef = useRef<HTMLDivElement | null>(null);
    usePopupEditorKeyboard(containerRef, handleSave, handleCancel);

    return (
      <Box
        ref={containerRef}
        sx={{
          p: 2,
          width: 440,
          backgroundColor: "background.paper",
          boxShadow: 3,
          borderRadius: 1,
        }}
      >
        <Stack spacing={1.5}>
          <Typography variant="subtitle1">
            <strong>Edit Benefit Value</strong>
          </Typography>

          {customText.customTextSupported && (
            <CustomTextModeToggle
              editMode={customText.editMode}
              onChange={customText.setEditMode}
            />
          )}

          {!editingCustomText && (
            <TextField
              select
              size="small"
              label="Type"
              value={draftType}
              onChange={(e) => setDraftType(e.target.value)}
              fullWidth
            >
              {typeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}

          {!editingCustomText && (
            <BenefitDescriptionValueInputs
              valueKind={valueKind}
              showMinimum={showMinimum}
              maxEntries={maxEntries}
              minEntries={minEntries}
              showErrors={showErrors}
              hasError={hasError}
              single={draftSingle}
              onSingleChange={setDraftSingle}
              onSingleBlur={() => setShowErrors(true)}
              minimum={draftMinimum}
              onMinimumChange={setDraftMinimum}
              arrayEntries={arrayEntries}
              onArrayUpdate={updateArrayEntry}
              onArrayAdd={addArrayEntry}
              onArrayRemove={removeArrayEntry}
            />
          )}

          {editingCustomText && (
            <CustomTextEditorFields
              content={customText.draftCustomText}
              onUpdate={customText.setDraftCustomText}
              onCharacterCount={customText.setDraftCharacterCount}
            />
          )}

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>

            <Button
              size="sm"
              variant="primary"
              onClick={handleSave}
              disabled={!editingCustomText && hasError}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  },
);
